import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import { RefreshCw } from "lucide-react";
import { DocumentsFacetsNav } from "../../../facets";
import { DocumentTable } from "../DocumentTable";
import { useDocumentSearch, useWatchDocumentSearchFacets, useWatchDocumentSearchResult } from "../search/DocumentSearchContext";
import { DocumentSearchProvider } from "../search/DocumentSearchProvider";
import { ContentDispositionButton } from "./ContentDispositionButton";
import { Button, ErrorBox, Spinner, useIntersectionObserver } from "@vertesia/ui/core";
import { useUITranslation } from '../../../../i18n/index.js';
const layout = [
    { "name": "Name", "field": "properties.title", "type": "string", "fallback": "name" },
    { "name": "Type", "field": "type.name", "type": "string" },
    { "name": "Status", "field": "status", "type": "string" },
    { "name": "Created At", "field": "created_at", "type": "date" }
];
const LAST_DISPLAYED_VIEW = "vertesia.content_store.lastDisplayedView";
export function SelectDocument({ onChange, selectedIds }) {
    const onRowClick = (selected) => {
        onChange(selected || undefined);
    };
    return (_jsx(DocumentSearchProvider, { children: _jsx(SelectDocumentImpl, { onRowClick: onRowClick, selectedIds: selectedIds }) }));
}
function SelectDocumentImpl({ onRowClick, selectedIds }) {
    const { t } = useUITranslation();
    const highlightRow = useCallback((item) => !!selectedIds?.has(item.id), [selectedIds]);
    const [isReady, setIsReady] = useState(false);
    const [isGridView, setIsGridView] = useState(localStorage.getItem(LAST_DISPLAYED_VIEW) === "grid");
    const { search, isLoading, error, objects, hasMore } = useWatchDocumentSearchResult();
    const loadMoreRef = useRef(null);
    useIntersectionObserver(loadMoreRef, () => {
        if (isReady && hasMore && !isLoading) {
            setIsReady(false);
            search.loadMore(true)
                .finally(() => {
                setIsReady(true);
            });
        }
    }, { threshold: 0.1, deps: [isReady, hasMore, isLoading] });
    useEffect(() => {
        search.search()
            .finally(() => {
            setIsReady(true);
        });
    }, []);
    const facets = useWatchDocumentSearchFacets();
    const facetSearch = useDocumentSearch();
    const handleRefetch = () => {
        setIsReady(false);
        search.search().then(() => setIsReady(true));
    };
    if (error) {
        return _jsx(ErrorBox, { title: t('store.searchFailed'), children: error.message });
    }
    return (_jsxs("div", { className: "flex flex-col gap-2 h-full w-full", children: [_jsxs("div", { className: "flex justify-between items-center flex-shrink-0", children: [_jsx(DocumentsFacetsNav, { facets: facets, search: facetSearch }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Button, { variant: "outline", onClick: handleRefetch, alt: "Refresh", children: _jsx(RefreshCw, { size: 16 }) }), _jsx(ContentDispositionButton, { onUpdate: setIsGridView })] })] }), _jsxs("div", { className: "@container flex-1 overflow-y-auto", children: [_jsx(DocumentTable, { objects: objects || [], isLoading: false, layout: layout, onRowClick: onRowClick, highlightRow: selectedIds?.size ? highlightRow : undefined, isGridView: isGridView }), _jsx("div", { ref: loadMoreRef, className: "h-4 w-full" }), _jsx("div", { className: clsx("bg-white dark:bg-gray-800 opacity-80 absolute inset-0 z-50 flex justify-center items-center rounded", isLoading ? "block" : "hidden"), children: _jsx(Spinner, { size: "xl" }) })] })] }));
}
//# sourceMappingURL=SelectDocument.js.map